/**
 * **Lantai outlet: AREA, MEJA, dan PERANGKAT** — tiga hal yang gerbang kesiapan tuntut dan yang
 * tak punya satu pun layar sampai hari ini.
 *
 * Imron (2026-08-04): *"area/table crud belum ada"*.
 *
 * ### ⛔⛔ Tanpa layar ini, onboatding mandiri MENTOK — bukan karena ada langkah yang terlewat
 * `checkOutletReadiness` menolak membuka sesi kas bila outlet `table_service` tak punya satu pun
 * meja, dan `device_code` wajib pada tiap jejak (P10) sehingga terminal harus terdaftar. Rute
 * master `areas`, `tables`, dan `devices` sudah ada di server sejak lama; yang tak pernah ada
 * hanyalah layarnya. Pemilik yang menyusun outletnya sendiri sampai di gerbang itu lalu berhenti,
 * dan tak ada apa pun di portal yang bisa menolongnya.
 *
 * ### ⭐ Ketiganya dalam SATU layar, karena ketiganya satu pekerjaan
 * "Menyiapkan lantai outlet" dikerjakan sekali duduk: gambar areanya, taruh mejanya, daftarkan
 * terminalnya. Memecahnya jadi tiga menu memaksa orang berpindah bolak-balik sambil mengingat
 * outlet mana yang sedang ia kerjakan.
 *
 * ### ⚠️ Meja SEMU bukan meja
 * `table_kind` membedakan meja fisik dari penampung `virtual_takeaway`/`virtual_kiosk`. VTEC
 * memakai nomor ajaib '100' untuk maksud yang sama — dan angka ajaib berarti tak seorang pun bisa
 * menjawab "kenapa meja 100 tak pernah punya tamu?" tanpa bertanya ke orang lama.
 */
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table,
  Tag, Typography, message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEVICE_ROLES, deviceMayTender } from '@osbpos/contract';

import { deriveDeviceTier } from '@osbpos/contract';
import { api } from '../api/client';
import { PrinterProfileEditor } from '../components/PrinterProfileEditor';
import type { Outlet } from '../api/client';
import { canWrite } from '../api/session';
import { takeOutletContext } from '../layout/navigate';

/** ⛔ Dari KONTRAK — daftar salinan menua diam-diam (pelajaran `MC2`). */
const TABLE_KINDS = [
  'physical', 'virtual_quick', 'virtual_takeaway', 'virtual_delivery', 'virtual_kiosk',
] as const;

interface Area { readonly id: string; readonly outlet_id: string; readonly name: string }
interface Meja {
  readonly id: string; readonly area_id: string; readonly code: string;
  readonly seats: number; readonly table_kind: string; readonly status: string;
}
interface Perangkat {
  readonly id: string; readonly outlet_id: string; readonly device_code: string;
  readonly role: string; readonly name: string; readonly is_active: boolean;
  /** ⭐ Kapan terminal ini terakhir MENARIK katalog (menekan SYNC, atau dibuka ulang). */
  readonly catalog_applied_at?: string | null;
  readonly catalog_version?: string | null;
  /** ⭐ Setelan perangkat, termasuk `printer` (K6: printer = DATA). */
  readonly profile?: Record<string, unknown> | null;
  /** ⭐ Tier PENEMPATAN (K13) — `tablet` berarti perangkat ini menghitung bill sendiri. */
  readonly tier?: 'server' | 'tablet';
}

export function Floor() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);
  /** ⭐ Outlet yang sedang dilihat — dipakai membandingkan "terkirim" vs "ditarik" per perangkat. */
  const outletTerpilih = outlet.find((o) => o.id === outletId);
  /*
   * ⭐ Outlet awal boleh datang dari tautan pintas kesiapan (`navigate.ts`) — kalau tidak, layar ini
   * terbuka kosong dan orang yang baru saja mengklik "perbaiki di outlet X" harus memilihnya lagi
   * sambil menebak outlet mana tadi yang dimaksud.
   *
   * ⚠️ Diambil di EFEK, bukan di penginisialisasi `useState`. `StrictMode` menjalankan
   * penginisialisasi DUA KALI: panggilan pertama menghabiskan konteksnya, panggilan kedua dapat
   * `undefined` — dan yang kedua itulah yang dipakai. Gejalanya persis seperti fitur yang tak
   * pernah terpasang.
   */
  useEffect(() => {
    const dariTautan = takeOutletContext();
    if (dariTautan !== undefined) setOutletId(dariTautan);
  }, []);
  const [area, setArea] = useState<readonly Area[]>([]);
  const [meja, setMeja] = useState<readonly Meja[]>([]);
  const [alat, setAlat] = useState<readonly Perangkat[]>([]);
  /** ⭐ Kapabilitas outlet — bahan penurunan tier (K13). Dibaca di sini supaya aturannya sama
      persis dengan yang dipakai server (`deriveDeviceTier` di kontrak). */
  const [kapabilitasOutlet, setKapabilitasOutlet] = useState<readonly string[]>([]);

  const [namaArea, setNamaArea] = useState('');
  const [formMeja] = Form.useForm();
  const [formAlat] = Form.useForm();

  useEffect(() => { void api.outlets(undefined, true).then(setOutlet).catch(() => undefined); }, []);

  const muat = useCallback(() => {
    if (outletId === null) {
      setArea([]); setMeja([]); setAlat([]); setKapabilitasOutlet([]); return;
    }
    void api.listMaster<Area>('areas', { outlet_id: outletId }).then(async (a) => {
      setArea(a);
      /* ⚠️ Meja milik AREA, bukan outlet — jadi ia ditarik per area lalu digabung. Menyaringnya
         di layar dari daftar seluruh tenant akan menampilkan meja outlet lain saat datanya besar. */
      const semua = await Promise.all(a.map((x) => api.listMaster<Meja>('tables', { area_id: x.id })));
      setMeja(semua.flat());
    }).catch(() => undefined);
    void api.listMaster<Perangkat>('devices', { outlet_id: outletId }).then(setAlat).catch(() => undefined);
    void api.outletCapabilities(outletId)
      .then((r) => { setKapabilitasOutlet(r.map((x) => x.capability)); })
      .catch(() => { setKapabilitasOutlet([]); });
  }, [outletId]);
  useEffect(() => { muat(); }, [muat]);

  const kini = outlet.find((o) => o.id === outletId) ?? null;
  const namaArea2 = (id: string) => area.find((a) => a.id === id)?.name ?? id;

  async function tambahArea(): Promise<void> {
    if (namaArea.trim() === '' || outletId === null) return;
    await jalankan(async () => {
      await api.createMaster('areas', { outletId, name: namaArea.trim() });
      setNamaArea('');
    });
  }

  const jalankan = async (f: () => Promise<unknown>) => {
    try { await f(); muat(); } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="floor">
      <Space>
        <Typography.Text strong>{t('outlets.title')}</Typography.Text>
        <Select<string>
          style={{ minWidth: 280 }}
          placeholder={t('structure.pickBrand')}
          data-testid="floor-outlet"
          {...(outletId === null ? {} : { value: outletId })}
          onChange={(v) => { setOutletId(v); }}
          options={outlet.map((o) => ({ value: o.id, label: `${o.code} — ${o.name}` }))}
        />
      </Space>

      {outletId === null ? (
        <Alert type="info" showIcon title={t('floor.pickOutlet')} data-testid="floor-pilih" />
      ) : (
        <>
          {/*
            ⭐⭐ Menyebut KENAPA meja wajib, dan hanya saat memang wajib. Outlet quick service tak
            butuh meja sama sekali — peringatan yang selalu muncul akan diabaikan justru saat ia
            benar-benar berlaku.
          */}
          {kini?.sale_mode === 'table_service' && meja.length === 0 && (
            <Alert type="warning" showIcon data-testid="floor-wajib-meja"
                   title={t('floor.tablesRequired')} />
          )}

          <Card size="small" title={t('floor.areas')} data-testid="area-kartu">
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              {/* ⚠️ Area dulu: meja tak bisa ada tanpa induknya, dan urutan layar mengikuti itu. */}
              <Alert type="info" showIcon title={t('floor.areaHint')} />
              <Space>
                <Input
                  style={{ width: 240 }}
                  placeholder={t('floor.areaName')}
                  value={namaArea}
                  disabled={!boleh}
                  data-testid="area-nama"
                  onChange={(e) => { setNamaArea(e.target.value); }}
                  onPressEnter={() => void tambahArea()}
                />
                {/*
                  ⛔⛔ **Tombolnya WAJIB ada.** Versi pertama layar ini hanya menerima Enter — dan
                  kotak isian tanpa tombol terbaca sebagai kotak yang rusak: orang mengetik, menekan
                  ke tempat lain, lalu menyimpulkan fiturnya tak jalan. Enter tetap bekerja untuk
                  yang tangannya sudah di papan ketik; tombol untuk semua orang lain.
                */}
                <Button
                  type="primary"
                  disabled={!boleh || namaArea.trim() === ''}
                  data-testid="area-simpan"
                  onClick={() => void tambahArea()}
                >
                  {t('crud.save')}
                </Button>
              </Space>

              <Table<Area>
                size="small"
                pagination={false}
                rowKey={(a) => a.id}
                dataSource={[...area]}
                locale={{ emptyText: <span data-testid="area-kosong">{t('floor.noArea')}</span> }}
                columns={[
                  {
                    title: t('master.name'),
                    render: (_: unknown, a: Area) => (
                      /*
                       * ⭐ Ganti nama LANGSUNG di barisnya. Area cuma punya satu kolom yang bisa
                       * berubah; membuka laci untuk satu kotak isian menambah dua ketukan tanpa
                       * menambah kejelasan apa pun.
                       */
                      <Typography.Text
                        editable={boleh ? {
                          onChange: (v) => {
                            if (v.trim() === '' || v.trim() === a.name) return;
                            void jalankan(() => api.updateMaster('areas', a.id, { name: v.trim() }));
                          },
                        } : false}
                        data-testid={`area-${a.name}`}
                      >
                        {a.name}
                      </Typography.Text>
                    ),
                  },
                  {
                    title: '',
                    width: 120,
                    render: (_: unknown, a: Area) => {
                      const dipakai = meja.filter((m) => m.area_id === a.id).length;
                      return dipakai > 0 ? (
                        /* ⛔ Area yang masih bermeja TAK BOLEH hilang — server pun menolaknya
                           (`referencedBy`), tapi menyebutnya di sini menghemat satu penolakan. */
                        <Tag data-testid={`area-terpakai-${a.name}`}>
                          {t('floor.areaInUse', { n: dipakai })}
                        </Tag>
                      ) : (
                        <Popconfirm
                          title={t('floor.deleteArea')}
                          okText={t('crud.save')}
                          cancelText={t('crud.cancel')}
                          onConfirm={() => void jalankan(() => api.deleteMaster('areas', a.id))}
                        >
                          <Button size="small" danger disabled={!boleh}
                                  data-testid={`area-hapus-${a.name}`}>
                            {t('floor.delete')}
                          </Button>
                        </Popconfirm>
                      );
                    },
                  },
                ]}
              />
            </Space>
          </Card>

          <Card size="small" title={t('floor.tables')} data-testid="meja-kartu">
            {area.length === 0 ? (
              <Alert type="info" showIcon title={t('floor.areaFirst')} data-testid="meja-butuh-area" />
            ) : (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <Form
                  form={formMeja}
                  layout="inline"
                  disabled={!boleh}
                  initialValues={{ tableKind: 'physical', seats: 4 }}
                  onFinish={(v: Record<string, unknown>) => {
                    void jalankan(async () => {
                      await api.createMaster('tables', {
                        areaId: v['areaId'],
                        code: String(v['code']).toUpperCase(),
                        seats: Number(v['seats'] ?? 0),
                        tableKind: v['tableKind'],
                      });
                      formMeja.resetFields(['code']);
                    });
                  }}
                >
                  <Form.Item name="areaId" rules={[{ required: true }]}>
                    <Select style={{ width: 180 }} placeholder={t('floor.areas')}
                            data-testid="meja-area"
                            options={area.map((a) => ({ value: a.id, label: a.name }))} />
                  </Form.Item>
                  <Form.Item name="code" rules={[{ required: true }]}>
                    <Input style={{ width: 140 }} placeholder="T-01" data-testid="meja-kode" />
                  </Form.Item>
                  <Form.Item name="seats">
                    <InputNumber min={0} max={500} data-testid="meja-kursi" />
                  </Form.Item>
                  {/* ⚠️ Meja SEMU eksplisit — VTEC memakai nomor ajaib '100' untuk maksud ini. */}
                  <Form.Item name="tableKind">
                    <Select style={{ width: 200 }} data-testid="meja-jenis"
                            options={TABLE_KINDS.map((k) => ({ value: k, label: k }))} />
                  </Form.Item>
                  <Form.Item>
                    {/* ⛔ `Button` AntD, bukan <button> ber-kelas `ant-btn` yang ditiru tangan:
                        kelas Ant bisa berganti antar versi, dan tombol yang "kebetulan mirip"
                        kehilangan keadaan loading/disabled-nya tanpa ada yang menyadarinya. */}
                    <Button type="primary" htmlType="submit" data-testid="meja-simpan">
                      {t('crud.save')}
                    </Button>
                  </Form.Item>
                </Form>

                <Table<Meja>
                  size="small"
                  pagination={false}
                  rowKey={(m) => m.id}
                  dataSource={[...meja]}
                  locale={{ emptyText: <span data-testid="meja-kosong">{t('floor.noTable')}</span> }}
                  columns={[
                    {
                      title: t('master.code'), width: 140,
                      render: (_: unknown, m: Meja) => <b data-testid={`meja-${m.code}`}>{m.code}</b>,
                    },
                    { title: t('floor.areas'), width: 180,
                      render: (_: unknown, m: Meja) => namaArea2(m.area_id) },
                    {
                      title: t('floor.seats'),
                      width: 120,
                      render: (_: unknown, m: Meja) => (
                        /*
                         * ⭐ Kursi disunting langsung di baris. Server hanya membolehkan `seats`
                         * berubah — kode meja BEKU (ia tercetak di struk & dipakai pelayan
                         * menyebut mejanya), dan `table_kind` beku karena mengubah meja fisik jadi
                         * meja semu mengubah arti seluruh riwayatnya.
                         */
                        <Typography.Text
                          editable={boleh ? {
                            onChange: (v) => {
                              const n = Number(v.replace(/[^\d]/g, ''));
                              if (!Number.isFinite(n) || n === m.seats) return;
                              void jalankan(() => api.updateMaster('tables', m.id, { seats: n }));
                            },
                          } : false}
                          data-testid={`meja-kursi-${m.code}`}
                        >
                          {String(m.seats)}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: t('floor.kind'), width: 200,
                      render: (_: unknown, m: Meja) =>
                        m.table_kind === 'physical'
                          ? <Tag color="green">{m.table_kind}</Tag>
                          /* ⭐ Meja semu ditandai BERBEDA: ia tak pernah punya tamu duduk, dan
                             menyamakannya dengan meja fisik membuat laporan okupansi berbohong. */
                          : <Tag color="purple" data-testid={`meja-semu-${m.code}`}>{m.table_kind}</Tag>,
                    },
                    {
                      title: '',
                      width: 110,
                      render: (_: unknown, m: Meja) => (
                        /*
                         * ⚠️ Tombolnya SELALU ditawarkan, tak seperti area. Meja yang sudah pernah
                         * dipakai pesanan ditolak server (`referencedBy` → order), tapi layar tak
                         * bisa tahu itu tanpa menarik seluruh riwayat pesanan — dan menariknya demi
                         * menyembunyikan satu tombol jauh lebih mahal daripada satu penolakan yang
                         * menyebut sebabnya.
                         */
                        <Popconfirm
                          title={t('floor.deleteTable')}
                          okText={t('crud.save')}
                          cancelText={t('crud.cancel')}
                          onConfirm={() => void jalankan(() => api.deleteMaster('tables', m.id))}
                        >
                          <Button size="small" danger disabled={!boleh}
                                  data-testid={`meja-hapus-${m.code}`}>
                            {t('floor.delete')}
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Space>
            )}
          </Card>

          <Card size="small" title={t('floor.devices')} data-testid="alat-kartu">
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              {/* ⛔ P10: perangkat dialamati IDENTITAS (`device_code`), bukan IP. Disebut di layar
                  supaya tak ada yang mengetikkan alamat IP ke kolom kode. */}
              <Alert type="info" showIcon title={t('floor.deviceHint')} />
              <Form
                form={formAlat}
                layout="inline"
                disabled={!boleh}
                initialValues={{ role: 'pos_terminal' }}
                onFinish={(v: Record<string, unknown>) => {
                  void jalankan(async () => {
                    await api.createMaster('devices', {
                      outletId,
                      deviceCode: String(v['deviceCode']).toUpperCase(),
                      role: v['role'],
                      name: String(v['name'] ?? v['deviceCode']),
                    });
                    formAlat.resetFields(['deviceCode', 'name']);
                  });
                }}
              >
                <Form.Item name="deviceCode" rules={[{ required: true }]}>
                  <Input style={{ width: 200 }} placeholder="POS-PB-PKB-01" data-testid="alat-kode" />
                </Form.Item>
                <Form.Item name="name">
                  <Input style={{ width: 180 }} placeholder={t('master.name')} data-testid="alat-nama" />
                </Form.Item>
                {/*
                  ⭐⭐ **`cashier` vs `pos_terminal` menentukan boleh-tidaknya perangkat ini
                  menerima uang** (0051) — hardware-nya boleh sama persis.

                  ⛔ Tanpa keterangan ini, keduanya cuma dua string mirip di dropdown, dan yang
                  salah pilih baru tahu saat kasir pertama ditolak membuka sesi kas — di pagi hari
                  buka. Pilihan yang akibatnya besar tak boleh dijelaskan cuma oleh namanya.
                */}
                <Form.Item name="role" tooltip={t('floor.roleHint')}>
                  <Select style={{ width: 200 }} data-testid="alat-peran"
                          options={DEVICE_ROLES.map((r) => ({
                            value: r,
                            label: deviceMayTender(r) ? `${r} — ${t('floor.mayTender')}` : r,
                          }))} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" data-testid="alat-simpan">
                    {t('crud.save')}
                  </Button>
                </Form.Item>
              </Form>

              <Table<Perangkat>
                size="small"
                pagination={false}
                rowKey={(d) => d.id}
                dataSource={[...alat]}
                locale={{ emptyText: <span data-testid="alat-kosong">{t('floor.noDevice')}</span> }}
                columns={[
                  {
                    title: t('master.code'), width: 220,
                    render: (_: unknown, d: Perangkat) =>
                      <b data-testid={`alat-${d.device_code}`}>{d.device_code}</b>,
                  },
                  {
                    title: t('master.name'),
                    render: (_: unknown, d: Perangkat) => (
                      <Typography.Text
                        editable={boleh ? {
                          onChange: (v) => {
                            if (v.trim() === '' || v.trim() === d.name) return;
                            void jalankan(() => api.updateMaster('devices', d.id, { name: v.trim() }));
                          },
                        } : false}
                        data-testid={`alat-nama-${d.device_code}`}
                      >
                        {d.name}
                      </Typography.Text>
                    ),
                  },
                  {
                    title: t('floor.role'),
                    width: 180,
                    render: (_: unknown, d: Perangkat) => (
                      /*
                       * ⛔ Peran TAK BISA diubah, dan server memang tak menerimanya (`update` hanya
                       * `name` + `profile`). Terminal kasir yang berubah jadi KDS meninggalkan
                       * jejak transaksi yang menunjuk perangkat yang perannya sudah lain — dan
                       * pertanyaan "siapa yang menjual ini?" jadi tak terjawab. Ganti perangkatnya,
                       * jangan ganti perannya.
                       */
                      <Tag data-testid={`alat-peran-${d.device_code}`}>{d.role}</Tag>
                    ),
                  },
                  {
                    /*
                     * ⭐⭐⭐ **Mode hitung — DITURUNKAN, bukan disetel.**
                     *
                     * Sempat jadi pemilih manual selama satu jam pada 2026-08-23, lalu dibuang:
                     * Imron menolaknya (*"kenapa kamu membuat sesuatu yang mudah jadi
                     * complicated?"*), dan benar — seluruh bahan jawabannya sudah ada di server.
                     *
                     * ⇒ Ditampilkan sebagai HASIL supaya HQ tetap bisa melihat terminal mana yang
                     *   mode apa, tanpa ada yang perlu menyetelnya. Aturannya hidup di kontrak
                     *   (`deriveDeviceTier`), satu tempat untuk portal & server.
                     *
                     * ⚠️ Yang ditampilkan di sini adalah tier saat perangkat bicara ke PORTAL.
                     * Perangkat yang dilayani mini-PC selalu `server`, dan itu tak terlihat dari
                     * sini — mini-PC tak melapor ke portal soal siapa yang menempel padanya.
                     */
                    title: t('floor.tier.column'),
                    width: 150,
                    render: () => {
                      const tier = deriveDeviceTier({
                        servedByOutletServer: false,
                        saleMode: outletTerpilih?.sale_mode ?? 'quick_service',
                        capabilities: kapabilitasOutlet,
                      });
                      return (
                        <Tag data-testid="alat-tier" color={tier === 'tablet' ? 'blue' : 'default'}>
                          {t(`floor.tier.${tier}`)}
                        </Tag>
                      );
                    },
                  },
                  {
                    /*
                     * ⭐⭐⭐ **Profil printer** — sampai 2026-08-23 `device.profile.printer` dibaca
                     * `buildPrintJob` (K27) dan diterima PATCH master, tapi TAK ADA satu pun layar
                     * untuk mengisinya. Terlihat di server live: ketiga perangkat PB-PKB tak punya
                     * profil sama sekali ⇒ kasir menekan Cetak, server mencatat (201), byte-nya tak
                     * pernah dirakit. Setelan yang tak punya layar sama saja dengan yang tak ada.
                     */
                    title: t('floor.printer.column'),
                    width: 130,
                    render: (_: unknown, d: Perangkat) => (
                      <PrinterProfileEditor
                        deviceId={d.id}
                        deviceCode={d.device_code}
                        profile={d.profile ?? null}
                        disabled={!boleh}
                        onSaved={muat}
                      />
                    ),
                  },
                  {
                    /*
                     * ⭐⭐⭐ **Terminal mana yang masih pakai data lama.**
                     *
                     * Inilah yang VTEC tak punya: di sana, kalau kasir tak pernah menekan Get Data,
                     * TAK ADA satu pun yang tahu — terminal itu menjual harga lama sampai ada tamu
                     * protes, dan yang menemukannya orang yang paling tak bisa memperbaikinya.
                     *
                     * ⚠️ Dibandingkan dengan `catalog_published_at` OUTLET-nya, bukan dengan jam
                     * sekarang: terminal yang belum menarik selama seminggu itu normal kalau memang
                     * tak ada yang dikirim seminggu ini.
                     */
                    title: t('floor.lastSync'),
                    width: 170,
                    render: (_: unknown, d: Perangkat) => {
                      const kirim = outletTerpilih?.catalog_published_at ?? null;
                      const tarik = d.catalog_applied_at ?? null;
                      const tertinggal =
                        kirim !== null && (tarik === null || Date.parse(tarik) < Date.parse(kirim));
                      if (tarik === null) {
                        return (
                          <Tag color={tertinggal ? 'red' : 'default'}
                               data-testid={`alat-sync-${d.device_code}`}>
                            {t('floor.neverSynced')}
                          </Tag>
                        );
                      }
                      return (
                        <Tag color={tertinggal ? 'red' : 'green'}
                             data-testid={`alat-sync-${d.device_code}`}>
                          {tarik.slice(0, 16).replace('T', ' ')}
                          {tertinggal ? ` · ${t('floor.behind')}` : ''}
                        </Tag>
                      );
                    },
                  },
                  {
                    title: t('outlets.status'),
                    width: 130,
                    render: (_: unknown, d: Perangkat) => (
                      /*
                       * ⭐ Perangkat DINONAKTIFKAN, bukan dihapus — tablet yang hilang atau rusak
                       * tetap harus bisa dijelaskan saat jejak lama dibaca. Hapus permanen hanya
                       * untuk yang salah ketik dan belum pernah dipakai.
                       */
                      <Switch
                        size="small"
                        checked={d.is_active}
                        disabled={!boleh}
                        data-testid={`alat-aktif-${d.device_code}`}
                        checkedChildren={t('outlets.active')}
                        unCheckedChildren={t('outlets.inactive')}
                        onChange={(v) => {
                          void jalankan(() => api.setStatus('devices', d.id, v));
                        }}
                      />
                    ),
                  },
                  {
                    title: '',
                    width: 120,
                    /*
                     * ⭐⭐⭐ **Pasangkan** — pengganti menempel token lewat DevTools.
                     *
                     * ⛔ Kodenya ditampilkan SEKALI dan tak bisa diminta ulang: server menyimpan
                     * hash-nya, bukan nilainya. Menekan tombol ini lagi menerbitkan kode BARU dan
                     * mematikan yang lama — dua kode hidup untuk satu perangkat berarti yang lama,
                     * yang mungkin sudah terbaca orang lain, masih membuka pintu yang sama.
                     */
                    render: (_: unknown, d: Perangkat) => (
                      <Button
                        size="small"
                        disabled={!boleh}
                        data-testid={`alat-pasang-${d.device_code}`}
                        onClick={() => {
                          void (async () => {
                            try {
                              const r = await api.pairDevice(d.device_code);
                              Modal.info({
                                title: t('floor.pairTitle'),
                                content: (
                                  <div>
                                    <p>{t('floor.pairHint')}</p>
                                    <pre data-testid="kode-pasang" style={{ fontSize: 18 }}>{r.code}</pre>
                                  </div>
                                ),
                              });
                            } catch (e) {
                              message.error(e instanceof Error ? e.message : String(e));
                            }
                          })();
                        }}
                      >
                        {t('floor.pair')}
                      </Button>
                    ),
                  },
                  {
                    title: '',
                    width: 110,
                    render: (_: unknown, d: Perangkat) => (
                      <Popconfirm
                        title={t('floor.deleteDevice')}
                        description={t('floor.deleteDeviceHint')}
                        okText={t('crud.save')}
                        cancelText={t('crud.cancel')}
                        onConfirm={() => void jalankan(() => api.deleteMaster('devices', d.id))}
                      >
                        <Button size="small" danger disabled={!boleh}
                                data-testid={`alat-hapus-${d.device_code}`}>
                          {t('floor.delete')}
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            </Space>
          </Card>
        </>
      )}
    </Space>
  );
}
